import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { PanelPosition } from "../../utils/anchoredPosition";
import type { TourStepInteraction } from "../../types/tours";
import { canAutoPressAnchor } from "./tourAnchors";
import { TourBubble } from "./TourBubble";
import {
  findRenderedAnchorElement,
  isCommitInFlight,
  placeTourBubble,
  pressAnchorElement,
  scheduleFrame,
  TOUR_OVERLAY_ATTRIBUTE,
} from "./tourDom";
import { stepInteraction, type TourRun } from "./tourEngine";
import { SPOTLIGHT_PADDING, TourSpotlight } from "./TourSpotlight";
import { useAnchorState } from "./useAnchorState";
import { useStepInteraction } from "./useStepInteraction";
import { useTourModality } from "./useTourModality";
import "./TourOverlay.css";

/** Bubble distance from the anchor: the spotlight padding plus breathing room. */
const BUBBLE_GAP = SPOTLIGHT_PADDING + 8;

interface TourOverlayProps {
  run: TourRun;
  stepIndex: number;
  /** Steps of the next part of the walkthrough still to come (`TourBubble`). */
  stepsToFollow?: number;
  /** Render the step as a centered card even if its anchor is on the page. */
  suppressAnchor: boolean;
  /**
   * Steps this run has already had their press for, by the coach on a `ClickToContinue` step or by
   * the tour itself on an `OpenAnchor` one. Such a step is read-and-Next from then on, so coming
   * back to it never asks for — or repeats — a press that would close what it opened.
   */
  satisfiedStepKeys?: readonly string[];
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
  /**
   * The coach used the control a `ClickToContinue` step named. Advances the tour *and* records the
   * step as satisfied; defaults to a plain advance for a host that keeps no record.
   */
  onAnchorUsed?: () => void;
  /**
   * The tour pressed an `OpenAnchor` step's control itself. Records the step as satisfied without
   * advancing. A host that keeps no record simply presses once per mount of the step.
   */
  onAnchorOpened?: () => void;
  /**
   * The coach pressed a commit button (Attach, Save) while its modal is still open. The step waits
   * for the modal to close before it advances; this is the moment to note what was open.
   */
  onCommitPressed?: () => void;
}

/**
 * The spotlight and step bubble, portalled to `body` above every other layer (`--z-tour`), because
 * a flow-step tour has to cover a modal. Stays mounted across steps and chained tours.
 */
export const TourOverlay = ({
  run,
  stepIndex,
  stepsToFollow = 0,
  suppressAnchor,
  satisfiedStepKeys = [],
  onNext,
  onBack,
  onSkip,
  onAnchorUsed,
  onAnchorOpened,
  onCommitPressed,
}: TourOverlayProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  const step = run.steps[stepIndex] ?? run.steps[0];
  const stepKey = `${run.tour.key}:${step.key}`;
  const anchorId = suppressAnchor ? null : step.anchor;
  const { rect, expanded, label: anchorLabel } = useAnchorState(anchorId, stepKey);

  /*
   * One rule for what a step may do to the page, asked in one place.
   *
   * A step can only be interactive if it has a control to be interactive with, and the test for
   * that is the resolved anchor, not the anchor id: `rect` is null exactly when nothing on the page
   * carries the anchor (or it is hidden, or inside a closed panel). A centered card, a data tour
   * whose rows are gone (`suppressAnchor`), and a step whose control is not on screen are all
   * read-and-Next like every step was before this existed.
   *
   * Without that, a `ClickToContinue` step whose control never appears would lift the shield and
   * name a control that is not on the page, and Next would try to press it.
   *
   * On top of that, two things stop a step working the page when there is no press left in it, and
   * they agree by being the same decision: the run's own record that this step's press is already
   * spent — the coach's, on a `ClickToContinue` step, or the tour's own, on an `OpenAnchor` one —
   * and the control itself saying it is already open (`expanded`, measured in the same pass as
   * `rect`, so the two cannot disagree across a frame). Either is enough, and both branches ask
   * it, because the harm is the same on both: the controls these steps name are toggles, so
   * pressing "Columns" again *closes* the popover the next two steps stand inside, and a step that
   * is not going to press anything must not leave the page live and unshielded while the coach
   * reads it.
   *
   * Four of the five `ClickToContinue` anchors the manifest uses carry no `aria-expanded` at all,
   * so for those the run's record is the only one of the two that ever fires.
   *
   * And a step may only auto-press a control the allowlist knows. A step the allowlist refuses is
   * not interactive at all: it is not pressed, so the page must stay inert and shaded rather than
   * spotlighting a live, possibly destructive control in a hole in the shade.
   */
  const satisfied = satisfiedStepKeys.includes(step.key);
  const declared = anchorId && rect ? stepInteraction(step) : "None";
  const pressable =
    declared !== "OpenAnchor" || (anchorId !== null && canAutoPressAnchor(anchorId));
  const interaction: TourStepInteraction =
    declared === "None" || !pressable || satisfied || expanded ? "None" : declared;
  const isInteractive = interaction !== "None";

  useTourModality(rootRef, bubbleRef, onSkip, {
    interactive: isInteractive,
    interactiveAnchorId: interaction === "ClickToContinue" ? anchorId : null,
  });
  /**
   * The tour has pressed this step's control. Two things follow, and only the first is about state.
   *
   * The panel that just opened takes focus for itself (`ColumnSettingsPanel` and `ManageTeamsPanel`
   * both move it to their close button), which cuts the step's announcement short and leaves the
   * bubble's own buttons several tabs away on a step the coach was asked to do nothing on. A frame
   * later — after the panel's own focus effect has run — focus goes back to the step title, exactly
   * where every other step starts.
   */
  const handleAnchorOpened = useCallback(() => {
    onAnchorOpened?.();
    scheduleFrame(() => titleRef.current?.focus());
  }, [onAnchorOpened]);

  // After the modality hook, whose effects lift `inert` first: a programmatic click on an inert
  // control is exactly the thing this is meant not to depend on.
  const { disarm } = useStepInteraction({
    interaction,
    anchorId,
    stepKey,
    onAdvance: onAnchorUsed ?? onNext,
    onOpened: handleAnchorOpened,
    onCommitPressed,
  });

  /**
   * Next on a `ClickToContinue` step does what the step asked for: it presses the control as the
   * tour, then advances exactly as the coach's own press would. Every step has a Next button, so a
   * coach is never required to work the highlighted control to get through a tour. The press is
   * marked as the tour's own, so the step's click listener does not advance a second time. A control
   * the allowlist refuses is not pressed; Next then simply moves on.
   *
   * A commit step (Attach, Save) is never pressed. Its armed watch is stopped first, because `onNext`
   * closes the modal through its own close button (`closeCommitModal`), and the commit button
   * leaving the page must not then read as the coach's save landing and advance a second time.
   */
  const handleNext = useCallback(() => {
    // A save still landing keeps its watch: the step advances once the modal closes on success.
    if (isCommitInFlight(step)) return;
    disarm();
    if (interaction === "ClickToContinue" && anchorId && canAutoPressAnchor(anchorId)) {
      const element = findRenderedAnchorElement(anchorId);
      if (element) {
        pressAnchorElement(element);
        (onAnchorUsed ?? onNext)();
        return;
      }
    }
    onNext();
  }, [disarm, interaction, anchorId, onAnchorUsed, onNext, step]);

  const [position, setPosition] = useState<PanelPosition | null>(null);
  useLayoutEffect(() => {
    const bubble = bubbleRef.current;
    if (!rect || !bubble) {
      setPosition(null);
      return;
    }
    const next = placeTourBubble({
      anchor: rect,
      bubbleWidth: bubble.offsetWidth,
      bubbleHeight: bubble.offsetHeight,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      gap: BUBBLE_GAP,
    });
    setPosition((previous) =>
      previous && previous.top === next.top && previous.left === next.left ? previous : next
    );
  }, [rect, stepKey]);

  // Each step announces itself: focus lands on its title.
  useEffect(() => {
    titleRef.current?.focus();
  }, [stepKey]);

  /*
   * Popovers in this app close on a `mousedown` on `document` that landed outside themselves
   * (`useDismiss`, `ColumnSettingsPanel`, `SelectPopover` and a dozen more). The overlay is outside
   * every one of them, so pressing Next - or the shade taking a click - would shut the popup the
   * step is standing in front of, which is exactly what an interactive step just opened. Stopped in
   * the bubbling phase only, so the tour's own capture-phase listeners still see everything.
   */
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const stop = (event: Event) => event.stopPropagation();
    root.addEventListener("mousedown", stop);
    root.addEventListener("pointerdown", stop);
    return () => {
      root.removeEventListener("mousedown", stop);
      root.removeEventListener("pointerdown", stop);
    };
  }, []);

  return createPortal(
    <div
      ref={rootRef}
      // An interactive step lets pointer events through the shade to the control it spotlights;
      // the bubble takes its own back in CSS.
      className={`tour-overlay${isInteractive ? " tour-overlay--interactive" : ""}`}
      {...{ [TOUR_OVERLAY_ATTRIBUTE]: "" }}
    >
      <TourSpotlight rect={rect} />
      <TourBubble
        step={step}
        interaction={interaction}
        /*
          Named from the element the spotlight is on, so the bubble can say which control to press
          rather than "the highlighted control". Resolved in the same measurement pass as the rect
          (`useAnchorState`) rather than re-queried inside the bubble, which could find a different
          element from the one being spotlit.
        */
        anchorLabel={anchorLabel}
        isInteractive={isInteractive}
        stepIndex={stepIndex}
        stepCount={run.steps.length}
        stepOffset={run.stepOffset ?? 0}
        stepsToFollow={stepsToFollow}
        isNewSteps={run.mode === "new"}
        isPreview={run.preview === true}
        position={position}
        bubbleRef={bubbleRef}
        titleRef={titleRef}
        titleId={titleId}
        descriptionId={descriptionId}
        onNext={handleNext}
        onBack={onBack}
        onSkip={onSkip}
      />
    </div>,
    document.body
  );
};
